import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { DsIconComponent, DsIconSize } from '../icons/ds-icon.component';
import { DsIconName } from '../icons/ds-icons';

export type DsTagPillVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral'
  | 'new'
  | 'decorative1'
  | 'decorative2'
  | 'decorative3'
  | 'decorative4'
  | 'accent';
export type DsTagPillSize = 'xxs' | 'xs' | 's' | 'ms' | 'm';

@Component({
  selector: 'ds-tag-pill',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="ds-tag" [class]="hostClasses">
      @if (leadingIcon) {
        <ds-icon
          class="ds-tag__icon"
          [name]="leadingIcon"
          [size]="iconSize"
        />
      }
      <span class="ds-tag__label">{{ label }}</span>
      @if (trailingIcon) {
        <ds-icon
          class="ds-tag__icon"
          [name]="trailingIcon"
          [size]="iconSize"
        />
      }
    </span>
  `,
  styles: [`
    :host { display: contents; }

    .ds-tag {
      box-sizing:   border-box;
      display:      inline-flex;
      align-items:  center;
      justify-content: center;
      gap:          var(--ds-tag-pill-gap);
      border:       var(--ds-tag-pill-border-width) solid var(--_border);
      border-radius: var(--ds-tag-pill-border-radius);
      background:   var(--_bg);
      color:        var(--_text);
      font-family:  'Sura Sans', sans-serif;
      white-space:  nowrap;
    }

    .ds-tag__label {
      display: block;
      text-box-edge: cap alphabetic;
      text-box-trim: trim-both;
    }

    .ds-tag__icon {
      display: inline-flex;
      flex-shrink: 0;
      color: inherit;
    }

    /* ── Variants — light ───────────────────────────────────── */
    .ds-tag--success {
      --_bg:     var(--ds-tag-pill-success-background);
      --_border: var(--ds-tag-pill-success-border-color);
      --_text:   var(--ds-tag-pill-success-text-color);
    }
    .ds-tag--error {
      --_bg:     var(--ds-tag-pill-error-background);
      --_border: var(--ds-tag-pill-error-border-color);
      --_text:   var(--ds-tag-pill-error-text-color);
    }
    .ds-tag--warning {
      --_bg:     var(--ds-tag-pill-warning-background);
      --_border: var(--ds-tag-pill-warning-border-color);
      --_text:   var(--ds-tag-pill-warning-text-color);
    }
    .ds-tag--info {
      --_bg:     var(--ds-tag-pill-info-background);
      --_border: var(--ds-tag-pill-info-border-color);
      --_text:   var(--ds-tag-pill-info-text-color);
    }
    .ds-tag--neutral {
      --_bg:     var(--ds-tag-pill-neutral-background);
      --_border: var(--ds-tag-pill-neutral-border-color);
      --_text:   var(--ds-tag-pill-neutral-text-color);
    }
    .ds-tag--new {
      --_bg:     var(--ds-tag-pill-new-background);
      --_border: var(--ds-tag-pill-new-border-color);
      --_text:   var(--ds-tag-pill-new-text-color);
    }
    .ds-tag--decorative1 {
      --_bg:     var(--ds-tag-pill-decorative1-background);
      --_border: var(--ds-tag-pill-decorative1-border-color);
      --_text:   var(--ds-tag-pill-decorative1-text-color);
    }
    .ds-tag--decorative2 {
      --_bg:     var(--ds-tag-pill-decorative2-background);
      --_border: var(--ds-tag-pill-decorative2-border-color);
      --_text:   var(--ds-tag-pill-decorative2-text-color);
    }
    .ds-tag--decorative3 {
      --_bg:     var(--ds-tag-pill-decorative3-background);
      --_border: var(--ds-tag-pill-decorative3-border-color);
      --_text:   var(--ds-tag-pill-decorative3-text-color);
    }
    .ds-tag--decorative4 {
      --_bg:     var(--ds-tag-pill-decorative4-background);
      --_border: var(--ds-tag-pill-decorative4-border-color);
      --_text:   var(--ds-tag-pill-decorative4-text-color);
    }
    .ds-tag--accent {
      --_bg:     var(--ds-tag-pill-accent-background);
      --_border: var(--ds-tag-pill-accent-border-color);
      --_text:   var(--ds-tag-pill-accent-text-color);
    }

    /* ── Variants — invert ──────────────────────────────────── */
    .ds-tag--success.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-success-invert-background);
      --_border: var(--ds-tag-pill-success-invert-border-color);
      --_text:   var(--ds-tag-pill-success-invert-text-color);
    }
    .ds-tag--error.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-error-invert-background);
      --_border: var(--ds-tag-pill-error-invert-border-color);
      --_text:   var(--ds-tag-pill-error-invert-text-color);
    }
    .ds-tag--warning.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-warning-invert-background);
      --_border: var(--ds-tag-pill-warning-invert-border-color);
      --_text:   var(--ds-tag-pill-warning-invert-text-color);
    }
    .ds-tag--info.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-info-invert-background);
      --_border: var(--ds-tag-pill-info-invert-border-color);
      --_text:   var(--ds-tag-pill-info-invert-text-color);
    }
    .ds-tag--neutral.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-neutral-invert-background);
      --_border: var(--ds-tag-pill-neutral-invert-border-color);
      --_text:   var(--ds-tag-pill-neutral-invert-text-color);
    }
    .ds-tag--new.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-new-invert-background);
      --_border: var(--ds-tag-pill-new-invert-border-color);
      --_text:   var(--ds-tag-pill-new-invert-text-color);
    }
    .ds-tag--decorative1.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-decorative1-invert-background);
      --_border: var(--ds-tag-pill-decorative1-invert-border-color);
      --_text:   var(--ds-tag-pill-decorative1-invert-text-color);
    }
    .ds-tag--decorative2.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-decorative2-invert-background);
      --_border: var(--ds-tag-pill-decorative2-invert-border-color);
      --_text:   var(--ds-tag-pill-decorative2-invert-text-color);
    }
    .ds-tag--decorative3.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-decorative3-invert-background);
      --_border: var(--ds-tag-pill-decorative3-invert-border-color);
      --_text:   var(--ds-tag-pill-decorative3-invert-text-color);
    }
    .ds-tag--decorative4.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-decorative4-invert-background);
      --_border: var(--ds-tag-pill-decorative4-invert-border-color);
      --_text:   var(--ds-tag-pill-decorative4-invert-text-color);
    }
    .ds-tag--accent.ds-tag--invert {
      --_bg:     var(--ds-tag-pill-accent-invert-background);
      --_border: var(--ds-tag-pill-accent-invert-border-color);
      --_text:   var(--ds-tag-pill-accent-invert-text-color);
    }

    /* ── Sizes ──────────────────────────────────────────────── */
    .ds-tag--xxs {
      min-height:  var(--ds-tag-pill-xxs-min-height);
      padding:     var(--ds-tag-pill-xxs-padding-top) var(--ds-tag-pill-xxs-padding-horizontal) var(--ds-tag-pill-xxs-padding-bottom);
      font-size:   var(--ds-tag-pill-xxs-font-size);
      font-weight: var(--ds-tag-pill-xxs-font-weight);
      line-height: var(--ds-tag-pill-xxs-line-height);
    }
    .ds-tag--xs {
      min-height:  var(--ds-tag-pill-xs-min-height);
      padding:     var(--ds-tag-pill-xs-padding-top) var(--ds-tag-pill-xs-padding-horizontal) var(--ds-tag-pill-xs-padding-bottom);
      font-size:   var(--ds-tag-pill-xs-font-size);
      font-weight: var(--ds-tag-pill-xs-font-weight);
      line-height: var(--ds-tag-pill-xs-line-height);
    }
    .ds-tag--s {
      min-height:  var(--ds-tag-pill-s-min-height);
      padding:     var(--ds-tag-pill-s-padding-top) var(--ds-tag-pill-s-padding-horizontal) var(--ds-tag-pill-s-padding-bottom);
      font-size:   var(--ds-tag-pill-s-font-size);
      font-weight: var(--ds-tag-pill-s-font-weight);
      line-height: var(--ds-tag-pill-s-line-height);
    }
    .ds-tag--ms {
      min-height:  var(--ds-tag-pill-ms-min-height);
      padding:     var(--ds-tag-pill-ms-padding-top) var(--ds-tag-pill-ms-padding-horizontal) var(--ds-tag-pill-ms-padding-bottom);
      font-size:   var(--ds-tag-pill-ms-font-size);
      font-weight: var(--ds-tag-pill-ms-font-weight);
      line-height: var(--ds-tag-pill-ms-line-height);
    }
    .ds-tag--m {
      min-height:  var(--ds-tag-pill-m-min-height);
      padding:     var(--ds-tag-pill-m-padding-top) var(--ds-tag-pill-m-padding-horizontal) var(--ds-tag-pill-m-padding-bottom);
      font-size:   var(--ds-tag-pill-m-font-size);
      font-weight: var(--ds-tag-pill-m-font-weight);
      line-height: var(--ds-tag-pill-m-line-height);
    }
  `],
})
export class DsTagPillComponent {
  @Input() label: string = 'Status tag';
  @Input() variant: DsTagPillVariant = 'success';
  @Input() size: DsTagPillSize = 'xs';
  @Input() invert = false;
  @Input() leadingIcon: DsIconName | string = '';
  @Input() trailingIcon: DsIconName | string = '';

  get hostClasses(): string {
    return [
      `ds-tag--${this.variant}`,
      `ds-tag--${this.size}`,
      this.invert ? 'ds-tag--invert' : '',
    ].filter(Boolean).join(' ');
  }

  get iconSize(): DsIconSize {
    return `var(--ds-tag-pill-${this.size}-icon-size)`;
  }
}
